import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

const isBrowser = typeof window !== 'undefined';

// ⚠️ MIGRATION 19/04/2026 — Nettoyage localStorage hérité
//
// Entre le 18 et le 19/04/2026, un fix intermédiaire (commit 87c71cdd,
// depuis annulé par 17b0fd78) a stocké des tokens Supabase Auth dans
// window.localStorage au lieu de window.sessionStorage.
//
// Pour les utilisateurs qui se sont connectés pendant cette fenêtre,
// leur localStorage contient encore une ancienne session qui :
//   - n'est plus lue par le nouveau client Supabase (qui lit sessionStorage)
//   - pourrait perturber le flow d'auth si Supabase change de storage
//   - occupe inutilement le localStorage et constitue une fuite de token
//
// Ce bloc purge les clés auth Supabase de localStorage UNE FOIS par
// navigateur, puis pose un flag pour ne plus jamais re-exécuter.
// Idempotent, silencieux, aucun impact si rien à nettoyer.
const CLEANUP_FLAG = 'aureus_ls_cleanup_v1_done';

function cleanupLegacyLocalStorage() {
  if (!isBrowser) return;
  try {
    if (window.localStorage.getItem(CLEANUP_FLAG) === '1') return;

    const keysToRemove = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k) continue;
      // Clés auth Supabase : préfixe `sb-` ou contient `supabase.auth`
      if (k.startsWith('sb-') || k.includes('supabase.auth')) {
        keysToRemove.push(k);
      }
    }
    keysToRemove.forEach(k => {
      try { window.localStorage.removeItem(k); } catch {}
    });

    window.localStorage.setItem(CLEANUP_FLAG, '1');
  } catch {
    // localStorage inaccessible (mode privé strict, quota plein, etc.) → ignorer
  }
}

cleanupLegacyLocalStorage();

// ⚠️ POLITIQUE SÉCURITÉ 18/04/2026 — Mot de passe requis à chaque nouvelle session :
// - storage = window.sessionStorage (et NON localStorage)
//   → sessionStorage est vidé automatiquement à la fermeture du navigateur
//   → au retour le lendemain ou nouvelle fenêtre : pas de session → login requis
// - persistSession: true pour garder la session pendant l'onglet ouvert
// - autoRefreshToken pour éviter les déconnexions pendant le travail
// - detectSessionInUrl pour récupérer la session après magic link / reset password
//
// Cette politique s'applique à TOUS les rôles (admin, clients, employés)
// conformément aux exigences RGPD Art. 32 pour les données RH/paie sensibles.

// Accès SÉCURISÉ à sessionStorage (fix Sentry SecurityError 12/06/2026) :
// sur certains navigateurs (mode privé strict, webviews, cookies bloqués),
// la SIMPLE LECTURE de window.sessionStorage lève SecurityError — avant tout
// getItem. Comme ce module est importé par le layout (donc par le blog public),
// chaque visiteur verrouillé générait un crash Sentry. On sonde dans un
// try/catch et on retombe sur un storage mémoire : zéro crash, session non
// persistée (la politique sécurité du 18/04 est respectée a fortiori — rien
// n'est écrit du tout pour ces visiteurs).
function safeAuthStorage() {
  try {
    const s = window.sessionStorage;
    const k = '__aureus_probe__';
    s.setItem(k, '1');
    s.removeItem(k);
    return s;
  } catch {
    const mem = new Map();
    return {
      getItem: (key) => (mem.has(key) ? mem.get(key) : null),
      setItem: (key, value) => { mem.set(key, String(value)); },
      removeItem: (key) => { mem.delete(key); },
    };
  }
}

export const supabase = (supabaseUrl && supabaseAnonKey)
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage: isBrowser ? safeAuthStorage() : undefined,
      },
    })
  : null;

// Server-side: vérifier auth + rôle
export async function getAuthUser(request) {
  if (!supabase) return null;
  const authHeader = request?.headers?.get?.('authorization');
  if (!authHeader?.startsWith('Bearer ')) return null;
  const token = authHeader.slice(7);
  const { data: { user }, error } = await supabase.auth.getUser(token);
  if (error || !user) return null;
  return user;
}
